import { Injectable, inject } from '@angular/core';
import {
    AggregateResponse,
    PlayResult,
    PlayResultDetail,
    PlayResultDetailsActionType,
    PlayResultDetailsCountsSearch,
    TypedReportingSearchField,
} from '@r1-compass-library/models';
import { PlayResultDetailsService } from '@r1-compass-library/services/reporting/play-result-details.service';
import { DateTime } from 'luxon';
import { Observable, catchError, from, map, mergeMap, of, reduce } from 'rxjs';

type SuccessCountResponse = { [key: string]: number };

/**
 * Service for getting the aggregated counts for the play dashboard
 */
@Injectable({
    providedIn: 'root',
})
export class GroupedCountsService {
    private playResultDetailService = inject(PlayResultDetailsService);

    groupedCounts(
        filter: string,
        selectedDivisionIds: number[],
        fieldAlias: string,
        fieldName: keyof PlayResultDetail,
        actionType?: PlayResultDetailsActionType
    ): Observable<AggregateResponse[]> {
        const searches = this.buildSearches(selectedDivisionIds, filter, fieldName, actionType);
        return this.getAllSearchResponseData(searches).pipe(
            map(counts => this.buildAggregateResponseData(counts, fieldAlias))
        );
    }

    private buildSearches(
        selectedDivisionIds: number[],
        filter: string,
        fieldName: keyof PlayResultDetail,
        actionType?: PlayResultDetailsActionType
    ): PlayResultDetailsCountsSearch[] {
        return selectedDivisionIds.map(divisionId => {
            const req: PlayResultDetailsCountsSearch = {
                executionStartRangeBegin: filter,
                executionStartRangeEnd: DateTime.now().endOf('day').toISO()!,
                divisionId: divisionId,
                actionType: actionType,
                fields: [
                    new TypedReportingSearchField<PlayResult>('playId', 'play_result'),
                    new TypedReportingSearchField<PlayResultDetail>(fieldName, 'play_result_detail', undefined, 'sum'),
                ],
                groupByFields: [new TypedReportingSearchField<PlayResult>('playId', 'play_result')],
            };
            return req;
        });
    }

    private getAllSearchResponseData(searches: PlayResultDetailsCountsSearch[]) {
        return from(searches).pipe(
            // Make the requests concurrently
            mergeMap(
                search => this.playResultDetailService.getJoinedAggregates(search) as Observable<SuccessCountResponse[]>
            ),

            // As the responses arrive asynchronously, add their contents to an array
            reduce(
                (allResponses, response) => (allResponses.push(...response), allResponses),
                [] as SuccessCountResponse[]
            ),

            // Default empty array for any errors caught
            catchError(() => of([]))
        );
    }

    private buildAggregateResponseData(counts: { [key: string]: number }[], fieldAlias: string): AggregateResponse[] {
        return counts.map(el => ({
            key: String(el['playId']),
            aggregate: el[fieldAlias],
        })) as AggregateResponse[];
    }
}
